import React, { useState, useEffect, useMemo } from 'react';
import { 
    collection, query, onSnapshot, doc, addDoc, 
    updateDoc, deleteDoc, orderBy, serverTimestamp 
} from 'firebase/firestore';
import { db, auth } from '../App';
import { appId } from '../config/firebaseConfig';
import DOMPurify from 'dompurify';
import TiptapEditor from '../components/tiptap/TiptapEditor';

// --- COMPONENTES AUXILIARES ---

const FormModal = ({ title, children, onClose, onSave }) => (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex justify-center items-center z-[110] p-4 font-sans backdrop-blur-sm">
        <div className="bg-slate-800 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[95vh] flex flex-col border border-slate-700/50 overflow-hidden animate-in zoom-in-95 duration-200">
            <header className="p-6 border-b border-slate-700/50 flex justify-between items-center bg-slate-800/50">
                <h3 className="font-black text-xl text-cyan-400 uppercase tracking-tighter">{title}</h3>
                <button onClick={onClose} className="text-slate-400 hover:text-white transition-colors">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
            </header>
            <div className="p-8 overflow-y-auto flex-grow space-y-6 custom-scrollbar bg-slate-900/20">{children}</div>
            <footer className="p-6 border-t border-slate-700/50 flex gap-4 bg-slate-800/50">
                <button type="button" onClick={onClose} className="flex-1 bg-slate-700 hover:bg-slate-600 text-slate-300 font-bold py-4 rounded-xl transition-all uppercase text-xs tracking-widest">Cancelar</button>
                <button type="button" onClick={onSave} className="flex-1 bg-cyan-600 hover:bg-cyan-500 text-white font-black py-4 rounded-xl transition-all uppercase text-xs tracking-widest shadow-lg shadow-cyan-900/20">Salvar Laudo</button>
            </footer>
        </div>
    </div>
);

const ConfirmModal = ({ isOpen, title, message, onConfirm, onCancel }) => {
    if (!isOpen) return null;
    return (
        <div className="fixed inset-0 bg-black bg-opacity-80 flex justify-center items-center z-[150] p-4 font-sans backdrop-blur-md">
            <div className="bg-slate-800 rounded-2xl shadow-2xl w-full max-w-md border border-slate-700/50 p-8 text-center animate-in fade-in zoom-in-95 duration-300">
                <div className="w-16 h-16 bg-red-500/10 rounded-full flex items-center justify-center mx-auto mb-6">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
                </div>
                <h3 className="text-2xl font-black text-white mb-2 uppercase tracking-tighter">{title}</h3>
                <p className="text-slate-400 mb-8 leading-relaxed text-sm">{message}</p>
                <div className="flex gap-4">
                    <button onClick={onCancel} className="flex-1 bg-slate-700 hover:bg-slate-600 text-white font-bold py-4 rounded-xl transition-all">Manter</button>
                    <button onClick={onConfirm} className="flex-1 bg-red-600 hover:bg-red-500 text-white font-black py-4 rounded-xl shadow-xl shadow-red-900/20 transition-all">Excluir</button>
                </div>
            </div>
        </div>
    );
};

// --- PÁGINA PRINCIPAL ---

export default function TechnicalReportsPage({ permissionLevel, allowedCompanies, uiItems = {} }) {
    const isAllowed = (itemId) => uiItems[itemId] !== false;
    const [reports, setReports] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [copyId, setCopyId] = useState(null);
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [formMode, setFormMode] = useState('add');
    const [selectedReport, setSelectedReport] = useState(null);
    const [reportToDelete, setReportToDelete] = useState(null);
    const [isPreviewOpen, setIsPreviewOpen] = useState(false);
    const [clients, setClients] = useState([]);
    const [isClientDropdownOpen, setIsClientDropdownOpen] = useState(false);
    const clientRef = React.useRef(null);
    
    // Busca os clientes para sugestão no campo Empresa
    useEffect(() => {
        const unsubContratos = onSnapshot(collection(db, '/artifacts/intranet-arcus-prod/public/data/clients'), (snap) => {
            const contratos = snap.docs.map(doc => ({ id: doc.id, ...doc.data() })).filter(c => c.ativo !== false);
            setClients(prev => {
                const other = prev.filter(c => !contratos.some(nc => nc.id === c.id));
                const all = [...other, ...contratos];
                return all.sort((a,b) => (a.nomeFantasia || a.name || '').localeCompare(b.nomeFantasia || b.name || ''));
            });
        });

        const unsubAvulsos = onSnapshot(collection(db, '/artifacts/intranet-arcus-prod/public/data/clients-avulsos'), (snap) => {
            const avulsos = snap.docs.map(doc => ({ id: doc.id, ...doc.data() })).filter(c => c.ativo !== false);
            setClients(prev => {
                const other = prev.filter(c => !avulsos.some(na => na.id === c.id));
                const all = [...other, ...avulsos];
                return all.sort((a,b) => (a.nomeFantasia || a.name || '').localeCompare(b.nomeFantasia || b.name || ''));
            });
        });

        return () => {
            unsubContratos();
            unsubAvulsos();
        };
    }, []);


    
    // Estado do formulário
    const [formData, setFormData] = useState({
        title: 'Laudo Técnico',
        clientName: '',
        osNumber: '',
        problemDescription: '',
        technicalAnalysis: '',
        diagnosis: '',
        cost: '',
        clientResponse: '',
        referenceLinks: []
    });

    const filteredClients = useMemo(() => {
        const base = allowedCompanies === null ? clients : clients.filter(c => allowedCompanies.includes(c.name) || allowedCompanies.includes(c.nomeFantasia));
        const search = (formData.clientName || '').toLowerCase();
        return base.filter(c => (c.nomeFantasia || c.name || '').toLowerCase().includes(search));
    }, [clients, allowedCompanies, formData.clientName]);

    // Fechar dropdown ao clicar fora
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (clientRef.current && !clientRef.current.contains(event.target)) {
                setIsClientDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const collectionPath = `/artifacts/${appId}/public/data/technical_reports`;

    useEffect(() => {
        const q = query(collection(db, collectionPath), orderBy('createdAt', 'desc'));
        const unsubscribe = onSnapshot(q, (snap) => {
            setReports(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
            setLoading(false);
        });
        return () => unsubscribe();
    }, [collectionPath]);

    // Função auxiliar para renderizar HTML seguro
    const renderRichText = (html) => {
        return { __html: DOMPurify.sanitize(html || '') };
    };

    // Função para remover tags HTML (usada na lista principal)
    const stripHtml = (html) => {
        const doc = new DOMParser().parseFromString(html || '', 'text/html');
        return doc.body.textContent || "";
    };

    const filteredReports = useMemo(() => {
        const lowerSearch = searchTerm.toLowerCase();
        let data = reports;

        // FILTRO DE GRUPO (Isolamento de Dados)
        if (allowedCompanies !== null) {
            data = data.filter(r => 
                allowedCompanies.includes(r.clientName) || 
                r.createdBy === auth.currentUser.email
            );
        }

        return data.filter(r => 
            r.clientName?.toLowerCase().includes(lowerSearch) || 
            r.osNumber?.toLowerCase().includes(lowerSearch) ||
            r.title?.toLowerCase().includes(lowerSearch)
        );
    }, [reports, searchTerm, allowedCompanies]);

    const handleOpenAdd = () => {
        setFormData({
            title: 'Laudo Técnico',
            clientName: '',
            osNumber: '',
            problemDescription: '',
            technicalAnalysis: '',
            diagnosis: '',
            cost: '',
            clientResponse: '',
            patrimony: '',
            hostname: '',
            referenceLinks: []
        });
        setFormMode('add');
        setIsFormOpen(true);
    };

    const handleOpenEdit = (report) => {
        setFormData({ 
            ...report, 
            technicalAnalysis: report.technicalAnalysis || report.technicalDiagnosis || '',
            diagnosis: report.diagnosis || '',
            referenceLinks: report.referenceLinks || []
        });
        setSelectedReport(report);
        setFormMode('edit');
        setIsFormOpen(true);
    };

    const handleSave = async () => {
        if (!formData.clientName.trim() || !formData.osNumber.trim()) {
            alert("Por favor, preencha o nome do cliente e o número da OS.");
            return;
        }

        try {
            if (formMode === 'edit') {
                const { id, ...dataToUpdate } = formData;
                await updateDoc(doc(db, collectionPath, id), {
                    ...dataToUpdate,
                    title: "Laudo Técnico",
                    updatedAt: serverTimestamp()
                });
            } else {
                await addDoc(collection(db, collectionPath), {
                    ...formData,
                    title: "Laudo Técnico",
                    createdAt: serverTimestamp(),
                    createdBy: auth.currentUser.email
                });
            }
            setIsFormOpen(false);
        } catch (error) {
            console.error("Erro ao salvar laudo:", error);
            alert("Erro ao salvar o laudo. Verifique o console.");
        }
    };

    const handleDelete = async () => {
        if (!reportToDelete) return;
        try {
            await deleteDoc(doc(db, collectionPath, reportToDelete.id));
            setReportToDelete(null);
        } catch (error) {
            console.error("Erro ao excluir:", error);
        }
    };

    const handlePrint = (report) => {
        setSelectedReport(report);
        setIsPreviewOpen(true);
    };

    const ensureUrl = (url) => {
        if (!url) return '';
        const trimmed = url.trim();
        if (!trimmed.startsWith('http://') && !trimmed.startsWith('https://')) {
            return `https://${trimmed}`;
        }
        return trimmed;
    };

    const handleCopyLink = (reportId) => {
        const link = `${window.location.origin}?public=laudo&reportId=${reportId}`;
        navigator.clipboard.writeText(link);
        setCopyId(reportId);
        setTimeout(() => setCopyId(null), 2000);
    };

    const executePrint = () => {
        setIsPreviewOpen(false);
        setTimeout(() => {
            window.print();
        }, 300);
    };

    const formatCurrency = (value) => {
        if (!value) return '';
        const cleanValue = value.replace(/\D/g, '');
        const options = { style: 'currency', currency: 'BRL' };
        return new Intl.NumberFormat('pt-BR', options).format(cleanValue / 100);
    };

    const handleCostChange = (e) => {
        const value = e.target.value;
        setFormData({ ...formData, cost: formatCurrency(value) });
    };

    const handleAddLink = () => {
        setFormData({
            ...formData,
            referenceLinks: [...formData.referenceLinks, { label: '', url: '' }]
        });
    };

    const handleLinkChange = (index, field, value) => {
        const updatedLinks = [...formData.referenceLinks];
        updatedLinks[index][field] = value;
        setFormData({ ...formData, referenceLinks: updatedLinks });
    };

    const handleRemoveLink = (index) => {
        const updatedLinks = formData.referenceLinks.filter((_, i) => i !== index);
        setFormData({ ...formData, referenceLinks: updatedLinks });
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-full">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500"></div>
            </div>
        );
    }

    return (
        <div className="relative p-4 md:p-8 font-sans text-white max-w-7xl mx-auto">
            
            {/* ESTILOS DE IMPRESSÃO */}
            <style>
                {`
                /* Estilos Base do Laudo (Compartilhados entre Print e Preview) */
                .report-container { 
                    display: block; 
                    width: 100%; 
                    background: white;
                    color: black;
                    position: relative;
                    min-height: 297mm; /* Mínimo de uma folha A4 */
                    font-family: 'Outfit', 'Inter', sans-serif;
                    box-sizing: border-box;
                    padding: 20mm;
                    z-index: 1;
                }
                
                /* Marca d'água (Garante repetição em todas as páginas no PDF) */
                .watermark-fixed {
                    position: absolute;
                    top: 50%;
                    left: 50%;
                    transform: translate(-50%, -50%);
                    width: 500px;
                    height: 500px;
                    background-image: url('/logo.png');
                    background-repeat: no-repeat;
                    background-position: center;
                    background-size: contain;
                    opacity: 0.10;
                    pointer-events: none;
                    z-index: -1;
                }

                @media print {
                    .watermark-fixed {
                        position: fixed; /* O segrego para repetir em cada página */
                        top: 50%;
                        left: 50%;
                        transform: translate(-50%, -50%);
                    }
                }
                
                .report-header, .report-section, .grid-2, .report-footer {
                    position: relative;
                    z-index: 10;
                    background: transparent;
                }

                .report-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: flex-start;
                    border-bottom: 3px solid #000;
                    padding-bottom: 30px;
                    margin-bottom: 40px;
                }
                
                .header-logo {
                    height: 65px;
                    object-fit: contain;
                    opacity: 1 !important;
                    filter: none !important;
                }
                
                .report-title-box { text-align: right; }
                .report-title { 
                    font-size: 32pt; 
                    font-weight: 900; 
                    margin: 0; 
                    text-transform: uppercase; 
                    color: #000; 
                    letter-spacing: -1px;
                    line-height: 1;
                }
                .report-subtitle { 
                    font-size: 11pt; 
                    color: #555; 
                    margin: 5px 0 0 0; 
                    font-weight: 500;
                    letter-spacing: 1px;
                }
                
                .report-section { margin-bottom: 30px; page-break-inside: avoid; }
                .section-label { 
                    font-size: 8.5pt; 
                    font-weight: 900; 
                    text-transform: uppercase; 
                    color: #000; 
                    border-bottom: 1.5px solid #000; 
                    padding-bottom: 6px; 
                    margin-bottom: 12px; 
                    letter-spacing: 0.5px;
                    white-space: nowrap;
                }
                .section-content { font-size: 11pt; line-height: 1.6; text-align: justify; color: #1e293b; }

                /* Estilos para o conteúdo formatado (Rich Text) */
                .prose { 
                    max-width: none;
                }
                .prose p { margin-bottom: 0.5em; }
                .prose ul { list-style-type: disc; padding-left: 1.5em; margin-bottom: 0.5em; }
                .prose ol { list-style-type: decimal; padding-left: 1.5em; margin-bottom: 0.5em; }
                .prose strong { font-weight: bold; }
                .prose em { font-style: italic; }
                .prose a { 
                    color: #0891b2; 
                    text-decoration: underline !important; 
                }
                .prose h1 { font-size: 1.5em; font-weight: bold; margin-top: 0.5em; }
                .prose h2 { font-size: 1.3em; font-weight: bold; margin-top: 0.5em; }
                .prose h3 { font-size: 1.1em; font-weight: bold; margin-top: 0.5em; }
                
                .grid-2 { 
                    display: grid; 
                    grid-template-columns: 1fr 1fr; 
                    gap: 30px; 
                }
                
                .grid-3 {
                    display: grid;
                    grid-template-columns: 1.5fr 1fr 1fr;
                    gap: 30px;
                }
                
                .grid-4 {
                    display: grid;
                    grid-template-columns: 2fr 1fr 1.8fr 1.2fr;
                    gap: 20px;
                }
                
                .report-footer { 
                    margin-top: 60px; 
                    border-top: 3px solid #000; 
                    padding-top: 40px; 
                    font-size: 10pt; 
                    text-align: center; 
                    color: #000; 
                }
                .signature-area {
                    display: flex;
                    justify-content: center;
                    margin-top: 40px;
                    margin-bottom: 60px;
                }
                .signature-box { 
                    text-align: center; 
                    padding: 15px 30px;
                    border: 2px solid #000;
                    position: relative;
                }
                .validation-badge {
                    background: #000;
                    color: #fff;
                    font-size: 7pt;
                    font-weight: 900;
                    padding: 2px 8px;
                    position: absolute;
                    top: -10px;
                    left: 50%;
                    transform: translateX(-50%);
                    white-space: nowrap;
                    letter-spacing: 1px;
                }
                .signature-line { 
                    width: 100%; 
                    margin: 0 auto 5px auto;
                    font-weight: 800; 
                    text-transform: uppercase;
                    font-size: 10pt;
                    color: #000;
                    letter-spacing: 0.5px;
                }
                .signature-subtext {
                    font-size: 9pt;
                    color: #333;
                    font-weight: 600;
                }
                
                .auth-box {
                    margin-top: 40px;
                    padding: 25px;
                    border: 1px solid #e2e8f0;
                    border-left: 10px solid #000;
                    border-radius: 4px;
                    background-color: #f8fafc;
                    text-align: left;
                }
                .auth-title {
                    font-weight: 900;
                    text-transform: uppercase;
                    font-size: 9.5pt;
                    margin-bottom: 10px;
                    color: #000;
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    letter-spacing: 0.5px;
                }
                .auth-id {
                    font-family: 'JetBrains Mono', 'Courier New', Courier, monospace;
                    font-weight: 700;
                    font-size: 10.5pt;
                    color: #000;
                    background: #fff;
                    border: 1px solid #e2e8f0;
                    padding: 6px 12px;
                    display: inline-block;
                    margin-top: 12px;
                    letter-spacing: 1px;
                    border-radius: 4px;
                }
                
                .links-section { margin-top: 25px; page-break-inside: avoid; }
                .link-item { 
                    color: #0891b2; 
                    text-decoration: underline !important; /* Essencial para reconhecimento em PDF */
                    font-weight: 700; 
                    margin-right: 20px; 
                    padding-bottom: 2px;
                    transition: all 0.2s;
                    cursor: pointer;
                }
                .link-item:hover {
                    color: #0e7490;
                    border-bottom-color: #0e7490;
                    opacity: 0.8;
                }

                /* Responsividade Mobile */
                @media screen and (max-width: 768px) {
                    .report-container {
                        padding: 10mm 15px;
                    }
                    .report-header {
                        flex-direction: column;
                        align-items: center;
                        text-align: center;
                        gap: 20px;
                    }
                    .report-title-box {
                        text-align: center;
                    }
                    .report-title {
                        font-size: 22pt;
                    }
                    .grid-2, .grid-3 {
                        grid-template-columns: 1fr;
                        gap: 20px;
                    }
                    .conclusion-group .grid-2 {
                        grid-template-columns: 1fr;
                    }
                    .section-content {
                        font-size: 10.5pt;
                    }
                    .signature-area {
                        margin-top: 30px;
                        margin-bottom: 40px;
                    }
                    .auth-box {
                        padding: 15px;
                    }
                    .auth-id {
                        font-size: 9pt;
                        word-break: break-all;
                    }
                }

                @media print {
                    @page { margin: 0; size: A4; }
                    * { 
                        -webkit-print-color-adjust: exact !important;
                        print-color-adjust: exact !important;
                    }
                    
                    /* Forçar que todos os containers pais permitam o fluxo de múltiplas páginas */
                    html, body, #root, main, div[class*="h-screen"], div[class*="overflow-hidden"], div[class*="flex-1"] { 
                        background: white !important; 
                        color: black !important; 
                        margin: 0 !important;
                        padding: 0 !important;
                        height: auto !important;
                        min-height: 0 !important;
                        overflow: visible !important;
                        position: static !important;
                        display: block !important;
                    }
                    
                    /* Esconder elementos desnecessários */
                    nav, aside, button, header.main-header, .no-print, .preview-modal-overlay, .preview-actions { 
                        display: none !important; 
                    }
                    
                    .print-only { 
                        display: block !important; 
                        position: relative !important;
                        width: 100% !important;
                    }
                    
                    .report-container {
                        padding: 20mm !important;
                        min-height: 100% !important;
                        display: block !important;
                        background: white !important;
                    }
                    
                    .report-watermark {
                        position: fixed !important; /* Mantém a marca d'água em todas as páginas */
                        z-index: 0 !important;
                    }
                    
                    /* Evitar quebras de página no meio de seções críticas */
                    .report-section, .grid-2, .auth-box, .signature-box, .conclusion-group {
                        page-break-inside: avoid;
                        break-inside: avoid;
                    }

                    .conclusion-group {
                        margin-top: 40px;
                    }
                }

                @media print {
                    .conclusion-group {
                        display: block !important;
                        break-before: page; /* Sempre começa em uma nova folha */
                        padding-top: 65mm !important; /* Ajustado para centralizar sem empurrar para a 3ª página */
                        min-height: 0 !important; /* Removido min-height excessivo */
                        margin-top: 0 !important;
                    }
                }
                .print-only { display: none; }
                
                /* Estilos para o Modal de Preview (não interferem na impressão) */
                .preview-modal-overlay {
                    position: fixed;
                    inset: 0;
                    background: rgba(0, 0, 0, 0.85);
                    backdrop-filter: blur(10px);
                    display: flex;
                    flex-direction: column; /* Botões em cima, laudo em baixo */
                    align-items: center;
                    z-index: 200;
                    padding: 0;
                    overflow-y: auto;
                }
                .preview-content-card {
                    background: #f1f5f9;
                    width: 100%;
                    max-width: 900px;
                    padding: 40px;
                    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5);
                    border-radius: 12px;
                    position: relative;
                }
                .preview-paper {
                    background: white;
                    width: 100%;
                    min-height: 297mm;
                    box-shadow: 0 10px 30px rgba(0,0,0,0.1);
                    margin: 0 auto;
                }
                .preview-actions {
                    position: sticky;
                    top: 0;
                    width: 100%;
                    display: flex;
                    justify-content: center;
                    gap: 16px;
                    padding: 25px;
                    z-index: 300;
                    background: rgba(15, 23, 42, 0.8);
                    backdrop-filter: blur(10px);
                    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
                }
                `}
            </style>

            {/* CONTEÚDO PARA IMPRESSÃO */}
            {selectedReport && (
                <div className="print-only report-container">
                    <div className="watermark-fixed"></div>
                    <div className="report-header">
                        <img src="/logo_full.png" alt="Logo" className="header-logo" />
                        <div className="report-title-box">
                            <h1 className="report-title">{selectedReport.title}</h1>
                            <p className="report-subtitle">Arcus IT Network Solutions</p>
                        </div>
                    </div>

                    <div className={selectedReport.patrimony || selectedReport.hostname ? "grid-4" : "grid-2"}>
                        <div className="report-section">
                            <div className="section-label">Cliente</div>
                            <div className="section-content font-bold" style={{ fontSize: '14pt' }}>{selectedReport.clientName}</div>
                        </div>
                        <div className="report-section">
                            <div className="section-label">OS</div>
                            <div className="section-content font-bold" style={{ fontSize: '14pt' }}>A{selectedReport.osNumber}</div>
                        </div>
                        {selectedReport.hostname && (
                            <div className="report-section">
                                <div className="section-label">Etiqueta | Hostname</div>
                                <div className="section-content font-bold" style={{ fontSize: '14pt' }}>{selectedReport.hostname}</div>
                            </div>
                        )}
                        {selectedReport.patrimony && (
                            <div className="report-section">
                                <div className="section-label">Patrimônio</div>
                                <div className="section-content font-bold" style={{ fontSize: '14pt' }}>{selectedReport.patrimony}</div>
                            </div>
                        )}
                    </div>

                    <div className="report-section">
                        <div className="section-label">Problema Relatado</div>
                        <div className="section-content prose prose-slate max-w-none" dangerouslySetInnerHTML={renderRichText(selectedReport.problemDescription)} />
                    </div>

                    <div className="report-section">
                        <div className="section-label">Análise Técnica e Constatações</div>
                        <div className="section-content prose prose-slate max-w-none" dangerouslySetInnerHTML={renderRichText(selectedReport.technicalAnalysis || selectedReport.technicalDiagnosis)} />
                    </div>

                    <div className="report-section">
                        <div className="section-label">Diagnóstico</div>
                        <div className="section-content prose prose-slate max-w-none" dangerouslySetInnerHTML={renderRichText(selectedReport.diagnosis)} />
                    </div>

                    <div className="conclusion-group">
                        <div className="grid-2">
                            <div className="report-section">
                                <div className="section-label">Custo de Manutenção Estimado</div>
                                <div className="section-content font-black text-2xl">{selectedReport.cost || 'R$ 0,00'}</div>
                            </div>
                            <div className="report-section">
                                <div className="section-label">Status / Resposta do Cliente</div>
                                <div className="section-content">{selectedReport.clientResponse || 'Pendente'}</div>
                            </div>
                        </div>

                        {(selectedReport.referenceLinks && selectedReport.referenceLinks.length > 0) && (
                            <div className="report-section links-section">
                                <div className="section-label">Referências de Preços / Justificativa</div>
                                <div className="section-content flex flex-wrap">
                                    {selectedReport.referenceLinks.map((link, idx) => (
                                        <a key={idx} href={ensureUrl(link.url)} target="_blank" rel="noopener noreferrer" className="link-item">
                                            {link.label}
                                        </a>
                                    ))}
                                </div>
                            </div>
                        )}

                        <div className="report-footer">
                            <div className="signature-area">
                                <div className="signature-box">
                                    <div className="validation-badge">DOCUMENTO VALIDADO</div>
                                    <div className="signature-line">{selectedReport.createdBy?.split('@')[0].toUpperCase() || 'SISTEMA ARCUS'}</div>
                                    <div className="text-[8pt] text-slate-500 font-medium mb-1">{selectedReport.createdBy}</div>
                                    <div className="signature-subtext">Responsável Técnico Arcus</div>
                                </div>
                            </div>
                            
                            <div className="auth-box">
                                <div className="auth-title">
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
                                    Autenticação do Sistema Arcus
                                </div>
                                <p style={{ margin: 0, color: '#333', fontSize: '9pt', lineHeight: '1.4' }}>
                                    Este laudo técnico é um documento original, gerado eletronicamente em <strong>{
                                        selectedReport.createdAt 
                                        ? `${selectedReport.createdAt.toDate().toLocaleDateString('pt-BR')} às ${selectedReport.createdAt.toDate().toLocaleTimeString('pt-BR')}`
                                        : `${new Date().toLocaleDateString('pt-BR')} às ${new Date().toLocaleTimeString('pt-BR')}`
                                    }</strong>. 
                                    A integridade deste relatório é garantida pelo banco de dados central da Arcus Informática sob o identificador único abaixo.
                                </p>
                                <div className="auth-id">ID: {selectedReport.id?.toUpperCase()}</div>
                            </div>
                            
                            <p style={{ marginTop: '30px', fontSize: '8pt', color: '#777', textAlign: 'center' }}>
                                Arcus Informática • intranet.arcusinformatica.com.br • {new Date().getFullYear()}
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {/* MODAL DE PREVIEW VISUAL */}
            {isPreviewOpen && selectedReport && (
                <div className="preview-modal-overlay no-print">
                    <div className="preview-actions">
                        <button 
                            onClick={() => setIsPreviewOpen(false)} 
                            className="bg-slate-700 hover:bg-slate-600 text-white px-8 py-3.5 rounded-xl font-bold transition-all shadow-2xl uppercase text-[10px] tracking-widest border border-slate-600"
                        >
                            Fechar Preview
                        </button>
                        <button 
                            onClick={executePrint} 
                            className="bg-cyan-600 hover:bg-cyan-500 text-white px-10 py-3.5 rounded-xl font-black transition-all shadow-2xl uppercase text-[10px] tracking-[0.15em] flex items-center gap-3 shadow-cyan-900/40"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2z" /></svg>
                            Confirmar e Imprimir Agora
                        </button>
                    </div>

                    <div className="preview-content-card no-print">
                        <div className="preview-paper">
                            <div className="report-container">
                                <div className="watermark-fixed"></div>
                                <div className="report-header">
                                    <img src="/logo_full.png" alt="Logo" className="header-logo" />
                                    <div className="report-title-box">
                                        <h1 className="report-title">{selectedReport.title}</h1>
                                        <p className="report-subtitle">Arcus IT Network Solutions</p>
                                    </div>
                                </div>

                                <div className={selectedReport.patrimony || selectedReport.hostname ? "grid-4" : "grid-2"}>
                                    <div className="report-section">
                                        <div className="section-label">Cliente</div>
                                        <div className="section-content font-bold" style={{ fontSize: '14pt' }}>{selectedReport.clientName}</div>
                                    </div>
                                    <div className="report-section">
                                        <div className="section-label">OS</div>
                                        <div className="section-content font-bold" style={{ fontSize: '14pt' }}>{selectedReport.osNumber}</div>
                                    </div>
                                    {selectedReport.hostname && (
                                        <div className="report-section">
                                            <div className="section-label">Etiqueta | Hostname</div>
                                            <div className="section-content font-bold" style={{ fontSize: '14pt' }}>{selectedReport.hostname}</div>
                                        </div>
                                    )}
                                    {selectedReport.patrimony && (
                                        <div className="report-section">
                                            <div className="section-label">Patrimônio</div>
                                            <div className="section-content font-bold" style={{ fontSize: '14pt' }}>{selectedReport.patrimony}</div>
                                        </div>
                                    )}
                                </div>

                                <div className="report-section">
                                    <div className="section-label">Problema Relatado</div>
                                    <div className="section-content prose prose-slate max-w-none" dangerouslySetInnerHTML={renderRichText(selectedReport.problemDescription)} />
                                </div>

                                <div className="report-section">
                                    <div className="section-label">Análise Técnica e Constatações</div>
                                    <div className="section-content prose prose-slate max-w-none" dangerouslySetInnerHTML={renderRichText(selectedReport.technicalAnalysis || selectedReport.technicalDiagnosis)} />
                                </div>

                                <div className="report-section">
                                    <div className="section-label">Diagnóstico</div>
                                    <div className="section-content prose prose-slate max-w-none" dangerouslySetInnerHTML={renderRichText(selectedReport.diagnosis)} />
                                </div>

                                <div className="conclusion-group">
                                    <div className="grid-2">
                                    <div className="report-section">
                                        <div className="section-label">Custo de Manutenção Estimado</div>
                                        <div className="section-content font-black text-2xl">{selectedReport.cost || 'R$ 0,00'}</div>
                                    </div>
                                    <div className="report-section">
                                        <div className="section-label">Status / Resposta do Cliente</div>
                                        <div className="section-content">{selectedReport.clientResponse || 'Pendente'}</div>
                                    </div>
                                </div>

                                {(selectedReport.referenceLinks && selectedReport.referenceLinks.length > 0) && (
                                    <div className="report-section links-section">
                                        <div className="section-label">Referências de Preços / Justificativa</div>
                                        <div className="section-content flex flex-wrap">
                                            {selectedReport.referenceLinks.map((link, idx) => (
                                                <a key={idx} href={ensureUrl(link.url)} target="_blank" rel="noopener noreferrer" className="link-item">
                                                    {link.label}
                                                </a>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                <div className="report-footer">
                                    <div className="signature-area">
                                        <div className="signature-box">
                                            <div className="validation-badge">DOCUMENTO VALIDADO</div>
                                            <div className="signature-line">{selectedReport.createdBy?.split('@')[0].toUpperCase() || 'SISTEMA ARCUS'}</div>
                                            <div className="text-[8pt] text-slate-500 font-medium mb-1">{selectedReport.createdBy}</div>
                                            <div className="signature-subtext">Responsável Técnico Arcus</div>
                                        </div>
                                    </div>
                                    
                                    <div className="auth-box">
                                        <div className="auth-title">
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
                                            Autenticação do Sistema Arcus
                                        </div>
                                        <p style={{ margin: 0, color: '#333', fontSize: '9pt', lineHeight: '1.4' }}>
                                            Este laudo técnico é um documento original, gerado eletronicamente em <strong>{
                                                selectedReport.createdAt 
                                                ? `${selectedReport.createdAt.toDate().toLocaleDateString('pt-BR')} às ${selectedReport.createdAt.toDate().toLocaleTimeString('pt-BR')}`
                                                : `${new Date().toLocaleDateString('pt-BR')} às ${new Date().toLocaleTimeString('pt-BR')}`
                                            }</strong>. 
                                            A integridade deste relatório é garantida pelo banco de dados central da Arcus Informática sob o identificador único abaixo.
                                        </p>
                                        <div className="auth-id">ID: {selectedReport.id?.toUpperCase()}</div>
                                    </div>
                                    
                                    <p style={{ marginTop: '30px', fontSize: '8pt', color: '#777', textAlign: 'center' }}>
                                        Arcus Informática • intranet.arcusinformatica.com.br • {new Date().getFullYear()}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            )}

            {/* CONTEÚDO DA TELA (WEB) */}
            <div className="no-print">
                <header className="main-header mb-12 flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
                    <div>
                        <h1 className="text-4xl md:text-6xl font-black text-white tracking-tighter uppercase italic">
                            Laudos <span className="text-cyan-400">Técnicos</span>
                        </h1>
                        <p className="text-slate-400 mt-2 font-medium tracking-wide">Gestão e geração de relatórios técnicos Arcus.</p>
                    </div>
                    
                    <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 w-full md:w-auto">
                        <div className="relative group">
                            <svg xmlns="http://www.w3.org/2000/svg" className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-500 group-focus-within:text-cyan-400 transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
                            <input 
                                type="text" 
                                placeholder="Pesquisar laudos..." 
                                value={searchTerm} 
                                onChange={e => setSearchTerm(e.target.value)} 
                                className="bg-slate-800/50 border border-slate-700/50 rounded-xl py-3.5 pl-12 pr-6 text-white w-full sm:w-64 focus:outline-none focus:ring-2 focus:ring-cyan-500/50 transition-all font-medium placeholder:text-slate-600" 
                            />
                        </div>
                        
                        {permissionLevel === 'edit' && (
                            <button 
                                onClick={handleOpenAdd} 
                                className="bg-cyan-600 hover:bg-cyan-500 text-white px-8 py-3.5 rounded-xl font-black uppercase text-xs tracking-[0.2em] shadow-xl shadow-cyan-900/20 transition-all active:scale-[0.98] flex items-center justify-center gap-2"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M12 4v16m8-8H4" /></svg>
                                Criar Laudo
                            </button>
                        )}
                    </div>
                </header>

                {filteredReports.length === 0 ? (
                    <div className="bg-slate-800/30 border border-slate-700/50 rounded-3xl p-16 text-center backdrop-blur-sm">
                        <div className="w-20 h-20 bg-slate-700/30 rounded-full flex items-center justify-center mx-auto mb-6">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                        </div>
                        <h3 className="text-xl font-bold text-white mb-2">Nenhum laudo encontrado</h3>
                        <p className="text-slate-500">Comece a criar relatórios técnicos profissionais agora mesmo.</p>
                    </div>
                ) : (
                    <div className="space-y-3">
                        {filteredReports.map(report => (
                            <div key={report.id} className="bg-slate-800/40 border border-slate-700/50 rounded-xl p-4 md:px-6 hover:border-cyan-500/50 transition-all group flex flex-col md:flex-row items-stretch md:items-center gap-4 shadow-lg backdrop-blur-sm">
                                <div className="flex items-center gap-4 flex-1 min-w-0">
                                    <div className="flex-shrink-0 flex flex-col gap-1 min-w-[100px]">
                                        <div className="bg-cyan-500/10 text-cyan-400 text-[10px] font-black uppercase px-2.5 py-2 rounded-md tracking-widest border border-cyan-500/20 text-center">
                                            OS {report.osNumber}
                                        </div>
                                        <div className="text-[8px] font-mono text-slate-500 text-center truncate px-1" title={`ID: ${report.id}`}>
                                            ID: {report.id?.substring(0, 8).toUpperCase()}...
                                        </div>
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <h3 className="text-lg font-black text-white truncate uppercase tracking-tight leading-tight mb-0.5">{report.clientName}</h3>
                                        <p className="text-slate-400 text-xs truncate italic">"{stripHtml(report.problemDescription)}"</p>
                                    </div>
                                </div>
                                
                                <div className="flex flex-row items-center justify-between md:justify-end gap-6 md:gap-8 flex-shrink-0 border-t md:border-t-0 border-slate-700/50 pt-4 md:pt-0">
                                    <div className="text-xl font-black text-white whitespace-nowrap">
                                        {report.cost || 'R$ 0,00'}
                                    </div>
                                    
                                    <div className="flex items-center gap-2">
                                        <button 
                                            onClick={() => handlePrint(report)} 
                                            className="bg-cyan-600 hover:bg-cyan-500 px-4 py-2.5 rounded-lg text-white transition-all shadow-lg flex items-center gap-2 text-[10px] font-black uppercase tracking-widest whitespace-nowrap"
                                            title="Gerar Laudo para Impressão"
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2z" /></svg>
                                            <span className="hidden lg:inline text-[9px]">Imprimir</span>
                                        </button>

                                        {isAllowed('generate-link') && (
                                            <button 
                                                onClick={() => handleCopyLink(report.id)} 
                                                className={`px-4 py-2.5 rounded-lg transition-all shadow-lg flex items-center gap-2 text-[10px] font-black uppercase tracking-widest whitespace-nowrap ${
                                                    copyId === report.id ? 'bg-emerald-600 text-white' : 'bg-slate-700 hover:bg-slate-600 text-cyan-400'
                                                }`}
                                                title="Gerar Link Público"
                                            >
                                                {copyId === report.id ? (
                                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>
                                                ) : (
                                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.826a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" /></svg>
                                                )}
                                                <span className="hidden lg:inline text-[9px]">{copyId === report.id ? 'Copiado!' : 'Gerar Link'}</span>
                                            </button>
                                        )}
                                        
                                        {permissionLevel === 'edit' && (
                                            <div className="flex gap-2">
                                                <button onClick={() => handleOpenEdit(report)} className="p-2.5 bg-slate-700 hover:bg-slate-600 text-slate-300 hover:text-cyan-400 transition-colors rounded-lg" title="Editar">
                                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.5L16.732 3.732z" /></svg>
                                                </button>
                                                <button onClick={() => setReportToDelete(report)} className="p-2.5 bg-slate-700 hover:bg-slate-600 text-slate-300 hover:text-red-400 transition-colors rounded-lg" title="Excluir">
                                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {/* MODAL DE FORMULÁRIO */}
                {isFormOpen && (
                    <FormModal 
                        title={formMode === 'add' ? "Novo Laudo Técnico" : "Editar Laudo"} 
                        onClose={() => setIsFormOpen(false)} 
                        onSave={handleSave}
                    >
                        <div className="space-y-1">
                            <label className="text-[10px] font-black text-slate-500 uppercase tracking-[0.2em] ml-1">Título do Documento (Fixo)</label>
                            <input 
                                type="text" 
                                value="Laudo Técnico" 
                                disabled
                                className="w-full bg-slate-800/50 border-none rounded-xl p-4 text-slate-400 font-bold cursor-not-allowed italic" 
                            />
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <div className="space-y-1">
                                <label className="text-[10px] font-black text-slate-500 uppercase tracking-[0.2em] ml-1 whitespace-nowrap">Número da OS Arcus</label>
                                <input 
                                    type="text" 
                                    placeholder="Ex: 5042"
                                    value={formData.osNumber} 
                                    onChange={e => setFormData({...formData, osNumber: e.target.value})} 
                                    className="w-full bg-slate-800 border-none rounded-xl p-4 text-white focus:ring-2 focus:ring-cyan-500/50 transition-all font-medium" 
                                />
                            </div>
                            <div className="space-y-1">
                                <label className="text-[10px] font-black text-slate-500 uppercase tracking-[0.2em] ml-1 whitespace-nowrap">Etiqueta | Hostname</label>
                                <input 
                                    type="text" 
                                    placeholder="Ex: ARCUS-TI-01"
                                    value={formData.hostname} 
                                    onChange={e => setFormData({...formData, hostname: e.target.value})} 
                                    className="w-full bg-slate-800 border-none rounded-xl p-4 text-white focus:ring-2 focus:ring-cyan-500/50 transition-all font-medium" 
                                />
                            </div>
                            <div className="space-y-1">
                                <label className="text-[10px] font-black text-slate-500 uppercase tracking-[0.2em] ml-1 whitespace-nowrap">Patrimônio (Caso tenha)</label>
                                <input 
                                    type="text" 
                                    placeholder="Ex: P-9042"
                                    value={formData.patrimony} 
                                    onChange={e => setFormData({...formData, patrimony: e.target.value})} 
                                    className="w-full bg-slate-800 border-none rounded-xl p-4 text-white focus:ring-2 focus:ring-cyan-500/50 transition-all font-medium" 
                                />
                            </div>
                        </div>
                        <div className="space-y-1 relative" ref={clientRef}>
                            <label className="text-[10px] font-black text-slate-500 uppercase tracking-[0.2em] ml-1">Cliente / Empresa</label>
                            <div className="relative">
                                <input 
                                    type="text" 
                                    placeholder="Nome completo do cliente ou empresa"
                                    value={formData.clientName} 
                                    onChange={e => {
                                        setFormData({...formData, clientName: e.target.value});
                                        setIsClientDropdownOpen(true);
                                    }} 
                                    onFocus={() => setIsClientDropdownOpen(true)}
                                    className="w-full bg-slate-800 border-none rounded-xl p-4 text-white focus:ring-2 focus:ring-cyan-500/50 transition-all font-medium" 
                                />
                                <div className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none">
                                    <svg xmlns="http://www.w3.org/2000/svg" className={`h-5 w-5 transition-transform duration-200 ${isClientDropdownOpen ? 'rotate-180' : ''}`} viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                                    </svg>
                                </div>
                            </div>
                            
                            {isClientDropdownOpen && filteredClients.length > 0 && (
                                <div className="absolute left-0 right-0 top-full mt-2 bg-slate-800 border border-slate-700 rounded-xl shadow-2xl z-[120] max-h-60 overflow-y-auto custom-scrollbar animate-in fade-in slide-in-from-top-2 duration-200 backdrop-blur-md">
                                    <div className="p-2 border-b border-slate-700/50 flex justify-between items-center bg-slate-800/80">
                                        <span className="text-[9px] font-black text-slate-500 uppercase tracking-widest ml-2">Clientes Sugeridos</span>
                                        <button 
                                            onClick={() => setIsClientDropdownOpen(false)}
                                            className="text-[9px] font-black text-cyan-400 hover:text-white uppercase tracking-widest px-2 py-1"
                                        >
                                            Fechar
                                        </button>
                                    </div>
                                    {filteredClients.map(c => (
                                        <div 
                                            key={c.id} 
                                            onClick={() => {
                                                setFormData({...formData, clientName: c.nomeFantasia || c.name});
                                                setIsClientDropdownOpen(false);
                                            }}
                                            className="px-4 py-3 hover:bg-cyan-600/20 hover:text-cyan-400 cursor-pointer transition-all border-b border-slate-700/30 last:border-0 flex items-center justify-between group"
                                        >
                                            <span className="font-bold text-sm tracking-tight">{c.nomeFantasia || c.name}</span>
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 opacity-0 group-hover:opacity-100 transition-opacity" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        <div className="space-y-1">
                            <label className="text-[10px] font-black text-slate-500 uppercase tracking-[0.2em] ml-1">Problema Relatado</label>
                            <TiptapEditor 
                                content={formData.problemDescription} 
                                onChange={content => setFormData({...formData, problemDescription: content})} 
                                minHeight="150px"
                            />
                        </div>

                        <div className="space-y-1">
                            <label className="text-[10px] font-black text-slate-500 uppercase tracking-[0.2em] ml-1">Análise Técnica e Constatações</label>
                            <TiptapEditor 
                                content={formData.technicalAnalysis} 
                                onChange={content => setFormData({...formData, technicalAnalysis: content})} 
                                minHeight="250px"
                            />
                        </div>

                        <div className="space-y-1">
                            <label className="text-[10px] font-black text-slate-500 uppercase tracking-[0.2em] ml-1">Diagnóstico</label>
                            <TiptapEditor 
                                content={formData.diagnosis} 
                                onChange={content => setFormData({...formData, diagnosis: content})} 
                                minHeight="150px"
                            />
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="space-y-1">
                                <label className="text-[10px] font-black text-slate-500 uppercase tracking-[0.2em] ml-1">Custo de Manutenção Estimado</label>
                                <input 
                                    type="text" 
                                    placeholder="R$ 0,00"
                                    value={formData.cost} 
                                    onChange={handleCostChange} 
                                    className="w-full bg-slate-800 border-none rounded-xl p-4 text-white focus:ring-2 focus:ring-cyan-500/50 transition-all font-black text-lg" 
                                />
                            </div>
                            <div className="space-y-1">
                                <label className="text-[10px] font-black text-slate-500 uppercase tracking-[0.2em] ml-1">Resposta do Cliente</label>
                                <input 
                                    type="text" 
                                    placeholder="Ex: Cliente negou a manutenção"
                                    value={formData.clientResponse} 
                                    onChange={e => setFormData({...formData, clientResponse: e.target.value})} 
                                    className="w-full bg-slate-800 border-none rounded-xl p-4 text-white focus:ring-2 focus:ring-cyan-500/50 transition-all font-medium" 
                                />
                            </div>
                        </div>

                        <div className="space-y-4 pt-4 border-t border-slate-700/50">
                            <div className="flex justify-between items-center">
                                <label className="text-[10px] font-black text-slate-500 uppercase tracking-[0.2em] ml-1">Links de Referências / Justificativa</label>
                                <button 
                                    type="button" 
                                    onClick={handleAddLink}
                                    className="text-[10px] font-black text-cyan-400 uppercase tracking-widest hover:text-white transition-colors flex items-center gap-1"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M12 4v16m8-8H4" /></svg>
                                    Adicionar Link
                                </button>
                            </div>
                            <div className="space-y-3">
                                {formData.referenceLinks.map((link, idx) => (
                                    <div key={idx} className="flex gap-3 bg-slate-900/30 p-3 rounded-xl border border-slate-700/30">
                                        <input 
                                            type="text" 
                                            placeholder="Nome do Link (Ex: Mercado Livre)" 
                                            value={link.label} 
                                            onChange={(e) => handleLinkChange(idx, 'label', e.target.value)}
                                            className="flex-1 bg-slate-800 border-none rounded-lg p-2.5 text-xs text-white focus:ring-1 focus:ring-cyan-500"
                                        />
                                        <input 
                                            type="text" 
                                            placeholder="URL (https://...)" 
                                            value={link.url} 
                                            onChange={(e) => handleLinkChange(idx, 'url', e.target.value)}
                                            className="flex-[2] bg-slate-800 border-none rounded-lg p-2.5 text-xs text-white focus:ring-1 focus:ring-cyan-500"
                                        />
                                        <button 
                                            type="button" 
                                            onClick={() => handleRemoveLink(idx)}
                                            className="text-slate-500 hover:text-red-500 transition-colors p-1"
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </FormModal>
                )}

                {/* MODAL DE CONFIRMAÇÃO DE EXCLUSÃO */}
                <ConfirmModal 
                    isOpen={!!reportToDelete} 
                    title="Excluir Laudo?" 
                    message={`Tem certeza que deseja excluir o laudo de "${reportToDelete?.clientName}"? Esta ação não pode ser desfeita.`}
                    onConfirm={handleDelete}
                    onCancel={() => setReportToDelete(null)}
                />
            </div>
        </div>
    );
}
